import React, { useState } from "react"
import { ComparisonChart, QualityBadge } from "./DashboardComponents"
import { DefinitionButton } from "./DefinitionExperience"
import { formatMoney, isFrequencyUnit, isImprovement, percentageLabel } from "./formatters"

const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return "Unavailable"
  return Number(value).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

const metricDecimals = metric => (
  isFrequencyUnit(metric.unit) ? 2
    : metric.unit === "percent" ? 1
      : 0
)

const metricValue = (metric, side) => {
  const value = metric?.[side]?.value
  if (metric.unit === "RON") return formatMoney(value, metric.unit)
  if (value === null || value === undefined) return "Unavailable"
  const formatted = formatNumber(value, metricDecimals(metric))
  return metric.unit === "percent" ? `${formatted}%` : formatted
}

const signedNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return "n/a"
  if (value === 0) return formatNumber(0, decimals)
  return `${value > 0 ? "+" : "−"}${formatNumber(Math.abs(value), decimals)}`
}

const deltaLabel = metric => (
  metric.delta?.points !== undefined && metric.delta.points !== null
    ? `${signedNumber(metric.delta.points, 1)} pp`
    : percentageLabel(metric.delta?.percentage)
)

const deltaDirection = metric => {
  const value = metric.delta?.points ?? metric.delta?.percentage ?? metric.delta?.absolute
  if (!Number.isFinite(value)) return ""
  return isImprovement(metric, value) ? "is-positive" : "is-negative"
}

const hierarchyLabel = metric => {
  if (metric.key === "netRevenue") return "North Star"
  if (metric.key === "confirmedParticipation") return "Leading indicator"
  return "Core driver"
}

const OverviewMetric = ({ metric }) => (
  <article className={`analytics-kpi-card analytics-overview-${metric.key}`}>
    <div className="analytics-kpi-topline">
      <span>{hierarchyLabel(metric)}</span>
      <QualityBadge quality={metric.current.quality} />
    </div>
    <h2>{metric.label}</h2>
    <strong className="analytics-kpi-value">{metricValue(metric, "current")}</strong>
    <div className="analytics-kpi-delta">
      <b className={deltaDirection(metric)}>{deltaLabel(metric)}</b>
      <span>vs {metricValue(metric, "comparison")}</span>
    </div>
    <p>{metric.definition?.summary}</p>
    <DefinitionButton metric={metric} />
  </article>
)

const fallbackRevenueDriver = snapshot => {
  const composition = snapshot.breakdowns?.netRevenueComposition
  const revenue = snapshot.revenue
  if (!composition || !revenue) return null
  const range = side => {
    const rows = composition.rows.map(row => ({
      key: row.key,
      label: row.label,
      value: row[side],
      delta: row.delta,
      share: Number.isFinite(row[side]) && Number.isFinite(revenue[side].netRevenue) && revenue[side].netRevenue !== 0
        ? (row[side] / revenue[side].netRevenue) * 100
        : null,
    }))
    const actual = rows.every(({ value }) => Number.isFinite(value))
      ? rows.reduce((total, row) => total + row.value, 0)
      : null
    const expected = revenue[side].netRevenue
    return {
      value: expected,
      rows,
      reconciliation: {
        expected,
        actual,
        discrepancy: Number.isFinite(actual) && Number.isFinite(expected) ? actual - expected : null,
      },
      quality: revenue[side].quality,
    }
  }
  return { current: range("current"), comparison: range("comparison") }
}

const signedMoney = value => {
  if (value === null || value === undefined) return "n/a"
  return `${value >= 0 ? "+" : "−"}${formatMoney(Math.abs(value))}`
}

const NetRevenueDrivers = ({ snapshot }) => {
  const driver = snapshot.drivers?.netRevenue || fallbackRevenueDriver(snapshot)
  if (!driver) return null
  const comparisonRows = new Map(driver.comparison.rows.map(row => [row.key, row]))
  const reconciled = driver.current.reconciliation.discrepancy === 0
    && driver.comparison.reconciliation.discrepancy === 0
  const mixedCurrent = snapshot.revenue?.current?.mixedEventCollections
  const mixedComparison = snapshot.revenue?.comparison?.mixedEventCollections
  const hasMixedEventCollections = snapshot.revenue?.current?.hasMixedEventCollections
    || snapshot.revenue?.comparison?.hasMixedEventCollections
    || [mixedCurrent, mixedComparison].some(value => Number.isFinite(value) && value !== 0)
  return (
    <section className="analytics-overview-driver analytics-net-revenue-driver" aria-labelledby="overview-revenue-drivers-title">
      <div className="analytics-section-heading">
        <div>
          <p className="analytics-eyebrow">Revenue drivers</p>
          <h2 id="overview-revenue-drivers-title">What drives Net Revenue</h2>
          <p>Includes Event Collections, Premium Revenue and Booking Platform Fee. Explicit Club Payable and unresolved collection stay outside the North Star.</p>
        </div>
        <QualityBadge quality={driver.current.quality} />
      </div>
      <div className="analytics-table-wrap">
        <table className="analytics-data-table analytics-overview-driver-table" aria-label="Net Revenue driver reconciliation">
          <thead><tr><th>Revenue source</th><th>Current</th><th>Comparison</th><th>Difference</th><th>Current share</th></tr></thead>
          <tbody>
            {driver.current.rows.map(row => {
              const comparison = comparisonRows.get(row.key)
              const difference = Number.isFinite(row.delta?.absolute)
                ? row.delta.absolute
                : Number.isFinite(row.value) && Number.isFinite(comparison?.value)
                  ? row.value - comparison.value
                  : null
              return (
                <tr key={row.key}>
                  <th>{row.label}</th>
                  <td>{formatMoney(row.value)}</td>
                  <td>{formatMoney(comparison?.value)}</td>
                  <td className={Number.isFinite(difference) ? (difference >= 0 ? "is-positive" : "is-negative") : ""}>{signedMoney(difference)}</td>
                  <td>{Number.isFinite(row.share) ? `${formatNumber(row.share, 1)}%` : "n/a"}</td>
                </tr>
              )
            })}
            <tr className="analytics-total-row">
              <th>Net Revenue</th>
              <td>{formatMoney(driver.current.value)}</td>
              <td>{formatMoney(driver.comparison.value)}</td>
              <td colSpan="2">{reconciled ? "Reconciled" : "Quality discrepancy"}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {hasMixedEventCollections && (
        <aside className="analytics-definition-note analytics-mixed-collection-note">
          <strong>Mixed Event Collections</strong>
          <p>
            {formatMoney(mixedCurrent)} of Mixed Event Collections is included in current Net Revenue ({formatMoney(mixedComparison)} comparison). It contains full legacy charges with both Sportya and club amounts; the club amount is reconciled separately and no historical split is inferred.
          </p>
        </aside>
      )}
      {snapshot.revenue?.current && (
        <div className="analytics-revenue-context" aria-label="Collection context outside Net Revenue">
          <span>Club Payable <strong>{formatMoney(snapshot.revenue.current.clubPayable)}</strong></span>
          <span>Unallocated Collection <strong>{formatMoney(snapshot.revenue.current.unallocatedCollection)}</strong></span>
        </div>
      )}
    </section>
  )
}

const ActivePlayerDrivers = ({ driver }) => {
  if (!driver) return null
  const comparisonRows = new Map(driver.comparison.rows.map(row => [row.key, row]))
  const reconciled = driver.current.reconciliation.discrepancy === 0
    && driver.comparison.reconciliation.discrepancy === 0
  const cohorts = [
    ["retained", "Retained"],
    ["new", "New in observed window"],
    ["reactivated", "Reactivated"],
    ["lost", "Lost"],
  ]
  return (
    <section className="analytics-overview-driver analytics-active-player-driver" aria-labelledby="overview-player-drivers-title">
      <div className="analytics-section-heading">
        <div>
          <p className="analytics-eyebrow">Engagement drivers</p>
          <h2 id="overview-player-drivers-title">What drives Active Players</h2>
          <p>Activity rows are unique within each source and non-additive across sources; the global result removes overlaps.</p>
        </div>
        <QualityBadge quality={driver.current.quality} />
      </div>
      <div className="analytics-table-wrap">
        <table className="analytics-data-table analytics-overview-driver-table" aria-label="Active Players activity breakdown">
          <thead><tr><th>Eligible activity</th><th>Current</th><th>Comparison</th><th>Interpretation</th></tr></thead>
          <tbody>
            {driver.current.rows.map(row => (
              <tr key={row.key}>
                <th>{row.label}</th>
                <td>{formatNumber(row.value)}</td>
                <td>{formatNumber(comparisonRows.get(row.key)?.value)}</td>
                <td>Unique in source</td>
              </tr>
            ))}
            <tr className="analytics-overlap-row">
              <th>Overlap removed</th>
              <td>{formatNumber(driver.current.overlapRemoved)}</td>
              <td>{formatNumber(driver.comparison.overlapRemoved)}</td>
              <td>Cross-activity duplicates</td>
            </tr>
            <tr className="analytics-total-row">
              <th>Deduplicated Active Players</th>
              <td>{formatNumber(driver.current.value)}</td>
              <td>{formatNumber(driver.comparison.value)}</td>
              <td>{reconciled ? "Reconciled" : "Quality discrepancy"}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {driver.cohorts && (
        <div className="analytics-cohort-grid" aria-label="Active Player cohort support values">
          {cohorts.map(([key, label]) => (
            <article key={key}>
              <span>{label}</span>
              <strong>{formatNumber(driver.cohorts[key])}</strong>
            </article>
          ))}
          <p>{driver.cohorts.quality?.reason}</p>
        </div>
      )}
    </section>
  )
}

const OverviewTrend = ({ snapshot }) => {
  const available = (snapshot.charts || []).filter(chart => (
    chart.key === "netRevenueTrend"
    || chart.key === "confirmedParticipationTrend"
    || chart.key === "activePlayersTrend"
  ))
  const aligned = available.filter(chart => chart.points?.length)
  const [selectedKey, setSelectedKey] = useState(
    aligned.find(({ key }) => key === "confirmedParticipationTrend")?.key || aligned[0]?.key
  )
  const selected = aligned.find(({ key }) => key === selectedKey) || aligned[0]
  if (!selected) {
    const chart = available.find(({ key }) => key === "confirmedParticipationTrend")
    const kpi = snapshot.kpis?.find(({ key }) => key === "confirmedParticipation")
    return chart && kpi ? <ComparisonChart chart={chart} kpi={kpi} /> : null
  }
  const title = selected.label.replace(/ trend$/i, "")
  const maximum = Math.max(
    ...selected.points.flatMap(point => [point.current.value, point.comparison.value]),
    1
  )
  const chartValue = value => selected.unit === "RON" ? formatMoney(value) : formatNumber(value)
  const chartDelta = value => selected.unit === "RON" ? signedMoney(value) : signedNumber(value)
  const plotDelta = value => {
    if (selected.unit !== "RON" || !Number.isFinite(value)) return signedNumber(value)
    const compact = Math.abs(value) >= 1000
      ? `${formatNumber(Math.abs(value) / 1000, 1)}k`
      : formatNumber(Math.abs(value), 0)
    return `${value >= 0 ? "+" : "−"}${compact}`
  }
  return (
    <section className="analytics-chart-card analytics-overview-trend" aria-labelledby="overview-trend-title">
      <div className="analytics-section-heading analytics-overview-trend-heading">
        <div>
          <p className="analytics-eyebrow">Aligned selected-period trend</p>
          <h2 id="overview-trend-title">{title}</h2>
          <p>Every bucket shows equivalent Current and Comparison coverage and the visible difference.</p>
        </div>
        <div className="analytics-chart-controls" aria-label="Overview trend metric">
          {aligned.map(chart => (
            <button
              type="button"
              key={chart.key}
              aria-pressed={chart.key === selected.key}
              onClick={() => setSelectedKey(chart.key)}
            >
              {chart.label.replace(/ trend$/i, "")}
            </button>
          ))}
        </div>
      </div>
      <div
        className="analytics-bucket-plot"
        role="img"
        aria-label={selected.accessibleSummary}
        style={{ "--analytics-bucket-count": selected.points.length }}
      >
        {selected.points.map(point => (
          <div className="analytics-bucket" key={point.key}>
            <div className="analytics-bucket-bars" aria-hidden="true">
              <b style={{ height: `${(point.current.value / maximum) * 100}%` }} />
              <b style={{ height: `${(point.comparison.value / maximum) * 100}%` }} />
            </div>
            <strong>{point.label}</strong>
            <span className={(point.delta.absolute || 0) >= 0 ? "is-positive" : "is-negative"}>
              {plotDelta(point.delta.absolute)}
            </span>
          </div>
        ))}
      </div>
      <div className="analytics-table-wrap">
        <table className="analytics-data-table analytics-trend-values" aria-label={`${title} aligned values`}>
          <thead><tr><th>Bucket</th><th>Current</th><th>Comparison</th><th>Difference</th><th>Difference %</th></tr></thead>
          <tbody>
            {selected.points.map(point => (
              <tr key={point.key}>
                <th>{point.label}</th>
                <td>{chartValue(point.current.value)}</td>
                <td>{chartValue(point.comparison.value)}</td>
                <td>{chartDelta(point.delta.absolute)}</td>
                <td>{percentageLabel(point.delta.percentage)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

const evidenceValue = (value, unit) => {
  if (!Number.isFinite(value)) return "Unavailable"
  if (unit === "RON") return formatMoney(value)
  if (unit === "percent" || unit === "percentage") return `${formatNumber(value, 1)}%`
  if (isFrequencyUnit(unit)) return formatNumber(value, 2)
  return formatNumber(value)
}

const evidenceDelta = support => {
  if (support.unit === "RON") return signedMoney(support.deltaAbsolute)
  if (support.unit === "percentage" && Number.isFinite(support.deltaAbsolute)) {
    return `${signedNumber(support.deltaAbsolute, 1)} pp`
  }
  if (Number.isFinite(support.deltaPercentage)) return percentageLabel(support.deltaPercentage)
  return signedNumber(support.deltaAbsolute, 1)
}

const Insights = ({ insights = [], onNavigate }) => {
  const visible = insights.slice(0, 3)
  if (!visible.length) return null
  return (
    <section className="analytics-overview-insights" aria-labelledby="overview-insights-title">
      <div className="analytics-section-heading">
        <div>
          <p className="analytics-eyebrow">What changed</p>
          <h2 id="overview-insights-title">Evidence-linked insights</h2>
          <p>Deterministic explanations only · maximum three.</p>
        </div>
      </div>
      <div className="analytics-insight-grid" aria-label="Deterministic insights">
        {visible.map(insight => (
          <article key={insight.key}>
            <p>{insight.statement}</p>
            <ul aria-label={`${insight.category || "Insight"} supporting values`}>
              {(insight.supportingValues || []).map(support => (
                <li key={support.metricKey}>
                  <strong>{support.label}</strong>
                  <span>
                    {evidenceValue(support.current, support.unit)} vs {evidenceValue(support.comparison, support.unit)} · {evidenceDelta(support)}
                  </span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              aria-label={insight.action.label}
              onClick={() => onNavigate?.({
                view: insight.action.view,
                ...(insight.action.region ? { region: insight.action.region } : {}),
              })}
            >
              {insight.action.label} <span aria-hidden="true">→</span>
            </button>
          </article>
        ))}
      </div>
    </section>
  )
}

const OperationalDrivers = ({ snapshot }) => {
  const premium = snapshot.premium?.metrics?.filter(({ key }) => (
    key === "activePremiumAccounts" || key === "paidPremiumSales"
  )) || []
  const booking = snapshot.booking?.metrics?.filter(({ key }) => (
    key === "elapsedConfirmedBookings" || key === "bookedCourtHours" || key === "uniqueBookingUsers"
  )) || []
  const metrics = [...premium, ...booking]
  if (!metrics.length) return null
  return (
    <section className="analytics-overview-driver analytics-operational-drivers" aria-labelledby="overview-operational-drivers-title">
      <div className="analytics-section-heading">
        <div>
          <p className="analytics-eyebrow">Operating drivers</p>
          <h2 id="overview-operational-drivers-title">Premium and Booking demand</h2>
          <p>Operational context that supports monetisation and repeat activity.</p>
        </div>
      </div>
      <div className="analytics-driver-list">
        {metrics.map(metric => (
          <article key={metric.key}>
            <span>{metric.label}</span>
            <strong>{metricValue(metric, "current")}</strong>
            <small>{deltaLabel(metric)} vs {metricValue(metric, "comparison")}</small>
          </article>
        ))}
      </div>
    </section>
  )
}

const OverviewView = ({ snapshot, onNavigate }) => {
  const headlineOrder = [
    "netRevenue",
    "confirmedParticipation",
    "activePlayers",
    "participationFrequency",
    "playerChurn",
    "eventPlayerChurn",
    "activeClubs",
  ]
  const metrics = headlineOrder
    .map(key => snapshot.kpis?.find(metric => metric.key === key))
    .filter(Boolean)
  return (
    <section className="analytics-overview-view">
      <section className="analytics-kpi-grid analytics-overview-kpis" aria-label="Headline indicators">
        {metrics.map(metric => <OverviewMetric metric={metric} key={metric.key} />)}
      </section>
      <OverviewTrend snapshot={snapshot} />
      <Insights insights={snapshot.insights} onNavigate={onNavigate} />
      <section className="analytics-overview-drivers" aria-labelledby="overview-drivers-title">
        <div className="analytics-view-heading">
          <div>
            <p className="analytics-eyebrow">Drivers</p>
            <h2 id="overview-drivers-title">What explains the result</h2>
            <p>Local activity breakdowns explain their parent KPI without filtering the dashboard.</p>
          </div>
        </div>
        <NetRevenueDrivers snapshot={snapshot} />
        <ActivePlayerDrivers driver={snapshot.drivers?.activePlayers} />
        <OperationalDrivers snapshot={snapshot} />
      </section>
    </section>
  )
}

export default OverviewView
